import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, Check, Info } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { SERVICE_MODULES } from '@/constants/content'

export const ServicesSection: FC = (): ReactNode => {
    return (
        <section
            id='leistungen'
            aria-labelledby='leistungen-title'
            className='border-y border-border bg-card py-24 sm:py-28'
        >
            <div className='mx-auto max-w-6xl px-6'>
                <SectionHeading
                    titleId='leistungen-title'
                    eyebrow='Leistungsmodule'
                    title='Hardware, Software und Begleitung aus einer Hand.'
                    description='Sie bekommen kein Paket zum Selbstaufbau, sondern ein laufendes Monitoring: von der Sensorplatzierung bis zur Übergabe der Dokumentation.'
                />

                <ul className='mt-14 grid gap-5 lg:grid-cols-3'>
                    {SERVICE_MODULES.map((module) => {
                        const Icon = module.icon

                        return (
                            <li
                                key={module.number}
                                className='reveal flex flex-col rounded-[1.75rem] border border-border bg-background/60 p-7'
                            >
                                <div className='flex items-start justify-between'>
                                    <span className='flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <span className='font-mono text-sm text-muted-foreground'>
                                        {module.number}
                                    </span>
                                </div>

                                <h3 className='mt-8 text-xl font-medium text-foreground'>
                                    {module.title}
                                </h3>

                                <p className='mt-2 text-pretty text-lg font-light leading-snug text-foreground/85'>
                                    {module.tagline}
                                </p>

                                <ul className='mt-6 grid gap-3 border-t border-border pt-6'>
                                    {module.points.map((point) => (
                                        <li key={point} className='flex gap-3 text-[0.95rem] text-muted-foreground'>
                                            <Check className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                                            {point}
                                        </li>
                                    ))}
                                </ul>

                                {module.note && (
                                    <p className='mt-auto flex gap-2 pt-6 text-xs leading-relaxed text-muted-foreground'>
                                        <Info className='mt-px size-3.5 shrink-0' aria-hidden='true' />
                                        {module.note}
                                    </p>
                                )}
                            </li>
                        )
                    })}
                </ul>

                <div className='mt-5 flex flex-col gap-5 rounded-[1.75rem] bg-accent px-7 py-6 sm:flex-row sm:items-center sm:justify-between'>
                    <p className='max-w-2xl text-pretty text-accent-foreground'>
                        <span className='font-medium'>Alles in einem projektbezogenen Angebot:</span> Sensorik, Plattformzugang, Inbetriebnahme und persönliche Betreuung.
                    </p>

                    <Link
                        href='/#kontakt'
                        className='group inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 text-[0.95rem] font-medium text-primary-foreground transition-colors hover:bg-primary/90'
                    >
                        Angebot anfragen
                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                    </Link>
                </div>
            </div>
        </section>
    )
}
